import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect } from 'react'
import { getLoadsByTagAndLoad } from '../services/loadService'
import type { LoadStates } from '../types/storageTypes'
import { timestampUtils } from '../utils/Timestamp'
import OpenMap from './OpenMaps'
import TimePicker from './Timepicker'

interface PickupLoadSectionProps {
  loadStates: LoadStates
  setLoadStates: React.Dispatch<React.SetStateAction<LoadStates>>
  tagId?: string
  loadNumber?: string
  isFinal?: boolean
  pickupLocation: string
}

const PickupLoadSection: React.FC<PickupLoadSectionProps> = ({
  loadStates,
  setLoadStates,
  tagId,
  loadNumber,
  isFinal = false,
  pickupLocation,
}) => {
  useEffect(() => {
    const loadData = async () => {
      const loadArrivingKey = `time_loadArriving_${tagId}_${loadNumber}`
      const loadDepartingKey = `time_loadDeparting_${tagId}_${loadNumber}`

      // First check localStorage
      const localLoadArriving = localStorage.getItem(loadArrivingKey)
      const localLoadDeparting = localStorage.getItem(loadDepartingKey)

      console.log('[PickupLoadSection] Checking localStorage with keys:', {
        loadArrivingKey,
        loadDepartingKey,
        loadArriving: localLoadArriving,
        loadDeparting: localLoadDeparting,
      })

      // If we have any data in localStorage, use it and don't fetch
      if (localLoadArriving || localLoadDeparting) {
        console.log('[PickupLoadSection] Found data in localStorage, using it')
        setLoadStates((prev) => ({
          ...prev,
          loadArriving: {
            timestamp: localLoadArriving || prev.loadArriving.timestamp,
            isActive: !!localLoadArriving,
          },
          loadDeparting: {
            timestamp: localLoadDeparting || prev.loadDeparting.timestamp,
            isActive: !!localLoadDeparting,
          },
        }))
        return
      }

      // If no localStorage data, fetch from API
      if (tagId && loadNumber) {
        console.log(
          '[PickupLoadSection] No localStorage data, fetching from API',
        )
        try {
          const loadData = await getLoadsByTagAndLoad(tagId, loadNumber)
          if (loadData) {
            console.log('[PickupLoadSection] Got API data:', loadData)
            setLoadStates((prev) => ({
              ...prev,
              loadArriving: {
                timestamp: timestampUtils.formatTimestamp(
                  loadData.arrival_time,
                ),
                isActive: !!loadData.arrival_time,
              },
              loadDeparting: {
                timestamp: timestampUtils.formatTimestamp(
                  loadData.departure_time,
                ),
                isActive: !!loadData.departure_time,
              },
            }))
          }
        } catch (error) {
          console.error('Failed to load data:', error)
        }
      }
    }

    loadData()
  }, [tagId, loadNumber, setLoadStates])

  const timeKeys = ['loadArriving', 'loadDeparting'] as const
  const labels = {
    loadArriving: 'Loading Arrive Time',
    loadDeparting: 'Loading Depart Time',
  } as const

  const renderTimeInput = (key: 'loadArriving' | 'loadDeparting') => {
    return (
      <TimePicker
        key={key}
        value={loadStates[key].timestamp}
        defaultTime={(() => {
          if (loadStates[key].timestamp) return undefined
          const now = new Date()
          let hours = now.getHours()
          const minutes = now.getMinutes()
          const period = hours >= 12 ? 'PM' : 'AM'
          hours = hours % 12 || 12
          const mm = minutes.toString().padStart(2, '0')
          return `${hours}:${mm} ${period}`
        })()}
        onChange={(newTime) => {
          if (isFinal) return

          localStorage.setItem(`time_${key}_${tagId}_${loadNumber}`, newTime)
          setLoadStates((prev) => ({
            ...prev,
            [key]: {
              timestamp: newTime,
              isActive: true,
            },
          }))
        }}
        label={labels[key]}
        buttonText={`Set ${labels[key]}`}
        disabled={isFinal}
      />
    )
  }

  return (
    <div className={css(styles.formContainer)}>
      <div className={css(styles.locationContainer)}>
        <h3 className={css(styles.locationLabel)}>PICK UP LOCATION</h3>
        <OpenMap
          address={pickupLocation}
          className={css(styles.locationText)}
        />
      </div>
      <div className={css(styles.loadButtons)}>
        {timeKeys.map(renderTimeInput)}
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  formContainer: {
    width: '90%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  locationContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  locationLabel: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '125%',
    color: '#6B7280',
    margin: 0,
  },
  locationText: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 600,
    color: '#1C64F2',
    margin: 0,
    marginBottom: '4px',
    marginTop: '4px',
  },
  loadButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: 24,
  },
})

export default PickupLoadSection
